
import {PatientFormValues} from '../types';
import {
    emptyContactIncome,
    PhoneType,
} from '@finpay-development/shared-platform/lib/esm/shared/model/primary-contact';
import {
    emptyPatientCurrentInsurance, emptyPatientWaiver,
} from '@finpay-development/shared-platform/lib/esm/patient/components/models/patient-view-model';
import {Utils} from '@finpay-development/shared-platform/lib/esm/shared/utils';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../../shared/state/root-reducer';

export const usePatientForm = () => {

    const selectedPatient = useSelector(
        (state: RootState) => state.patientContext.selectedPatient
    );
    const selectedPatientCurrentInsurance = useSelector(
        (state: RootState) => state.patientContext.selectedPatientCurrentInsurance
    );
    const employmentStatus = useSelector(
        (state: RootState) => {
            return state.metaData.employmentStatus;
        },
    );
    const selectedPatientWaiver = useSelector(
        (state: RootState) => state.patientContext.selectedPatientWaiver
    );
    const clientScholarshipsConfig= useSelector(
        (state: RootState) =>
            state.adminContext.adminConfigurationContext?.scholarships
    )

    let employmentStatusId: number | null

    switch(true){
        //Starting from empty form
        case !selectedPatient?.patientId && selectedPatient?.contact?.contactIncome?.isEmployed === false && !selectedPatient?.contact?.contactIncome?.employmentStatusId : {
            employmentStatusId = emptyContactIncome.employmentStatusId;
            break;
        }
        case selectedPatient?.patientId && selectedPatient?.contact?.contactIncome?.isEmployed === true && !selectedPatient?.contact?.contactIncome?.employmentStatusId : {
            const selectedEmploymentStatus = employmentStatus.find((status) => status.employmentStatusSlug === 'employed')
            employmentStatusId = selectedEmploymentStatus ? selectedEmploymentStatus.employmentStatusId :  emptyContactIncome.employmentStatusId
            break;
        }
        case selectedPatient?.patientId && selectedPatient?.contact?.contactIncome?.isEmployed === false && !selectedPatient?.contact?.contactIncome?.employmentStatusId : {
            const selectedEmploymentStatus = employmentStatus.find((status) => status.employmentStatusSlug === 'unemployed')
            employmentStatusId = selectedEmploymentStatus ? selectedEmploymentStatus.employmentStatusId :  emptyContactIncome.employmentStatusId
            break;
        }
        default:
            employmentStatusId = selectedPatient?.contact?.contactIncome?.employmentStatusId || emptyContactIncome.employmentStatusId
    }

    let selectedScholarshipConfig

    //todo : simplify below condition
    if(selectedPatientWaiver !== emptyPatientWaiver && selectedPatientWaiver.scholarshipPercentage >=0 && (typeof selectedPatientWaiver.fplRatio === 'number' && selectedPatientWaiver.fplRatio >=0) && (typeof selectedPatientWaiver.expenseIncomeRatio ==='number' && selectedPatientWaiver.expenseIncomeRatio>=0)){
        selectedScholarshipConfig = clientScholarshipsConfig.find((config) => {
            return config?.scholarshipPercentage == selectedPatientWaiver.scholarshipPercentage
        })
    }

    const initialValues: PatientFormValues = {

        clientId: selectedPatient?.clientId ? +selectedPatient.clientId : 0,
        patientId: selectedPatient?.patientId || 0,
        title: selectedPatient?.contact?.title || "-1",
        firstName: selectedPatient?.contact?.firstName || "",
        middleName: selectedPatient?.contact?.middleName ? selectedPatient?.contact?.middleName?.[0] : "",
        lastName: selectedPatient?.contact?.lastName || "",
        preferredName: selectedPatient?.contact?.preferredName || "",

        //Demographics
        dateOfBirth: selectedPatient?.contact?.birthDate || null,
        genderCode:
            selectedPatient?.contact?.genderCode === 'M' || selectedPatient?.contact?.genderCode === 'F'
                ? selectedPatient.contact.genderCode
                : '',
        email: selectedPatient?.contact?.email ||  "",
        firstPhone: selectedPatient?.contact?.primPhoneNum ? Utils.formatPhoneNumber(selectedPatient?.contact?.primPhoneNum) :  "",
        isFirstPhoneMobile: selectedPatient?.contact?.primPhoneType === PhoneType.mobile,
        secondPhone: selectedPatient?.contact?.secPhoneNum ? Utils.formatPhoneNumber(selectedPatient?.contact?.secPhoneNum) : "",
        isSecondPhoneMobile: selectedPatient?.contact?.secPhoneType === PhoneType.mobile,
        leaveVoicemail: selectedPatient?.contact?.leaveVMFl || true,
        receiveSMSMessages: selectedPatient?.contact?.smsConsentFl || true,
        streetAddress1: selectedPatient?.contact?.primaryAddress?.streetAddress1 ||  "",
        streetAddress2: selectedPatient?.contact?.primaryAddress?.streetAddress2 ||  "",
        city: selectedPatient?.contact?.primaryAddress?.city ||  "",
        state: selectedPatient?.contact?.primaryAddress?.state?.stateCode ||  "-1",
        zipCode: selectedPatient?.contact?.primaryAddress?.zipCode ||  "",
        zipCode4: selectedPatient?.contact?.primaryAddress?.zipCode4 ||  "",

        //Employment
        contactIncomeId: selectedPatient?.contact?.contactIncome?.contactIncomeId || emptyContactIncome.contactIncomeId,
        yearlySalary: selectedPatient?.contact?.contactIncome?.yearlySalary || emptyContactIncome.yearlySalary,
        employer: selectedPatient?.contact?.contactIncome?.employer || emptyContactIncome.employer,
        yearsAtEmployer: selectedPatient?.contact?.contactIncome?.yearsAtEmployer || emptyContactIncome.yearsAtEmployer,
        verificationScore: selectedPatient?.contact?.contactIncome?.verificationScore || emptyContactIncome.verificationScore,
        retirementIncome: selectedPatient?.contact?.contactIncome?.retirementIncome || emptyContactIncome.retirementIncome,
        capacityToPay: selectedPatient?.contact?.contactIncome?.capacityToPay || emptyContactIncome.capacityToPay,
        disposableIncome: selectedPatient?.contact?.contactIncome?.disposableIncome || emptyContactIncome.disposableIncome,
        businessIncome: selectedPatient?.contact?.contactIncome?.businessIncome || emptyContactIncome.businessIncome,
        wealthIncome: selectedPatient?.contact?.contactIncome?.wealthIncome || emptyContactIncome.wealthIncome,
        wagesAndEarnings: selectedPatient?.contact?.contactIncome?.wagesAndEarnings || emptyContactIncome.wagesAndEarnings,
        isEmployed: selectedPatient?.contact?.contactIncome?.isEmployed || emptyContactIncome.isEmployed,
        employmentStatusId,
        clientEmployerId: selectedPatient?.contact?.contactIncome?.clientEmployerId || emptyContactIncome.clientEmployerId,

        //Current Insurance
        patientCurrentInsuranceId: selectedPatientCurrentInsurance.patientCurrentInsuranceId || emptyPatientCurrentInsurance.patientCurrentInsuranceId,
        payorId: selectedPatientCurrentInsurance.payorId || emptyPatientCurrentInsurance.payorId,
        payorPlanId: selectedPatientCurrentInsurance.payorPlanId || emptyPatientCurrentInsurance.payorPlanId,
        subscriberName: selectedPatientCurrentInsurance.subscriberName || emptyPatientCurrentInsurance.subscriberName,
        subscriberRelation: selectedPatientCurrentInsurance.subscriberRelation > 0 ? selectedPatientCurrentInsurance.subscriberRelation : emptyPatientCurrentInsurance.subscriberRelation,
        memberId: selectedPatientCurrentInsurance.memberId || emptyPatientCurrentInsurance.memberId,
        groupNumber: selectedPatientCurrentInsurance.groupNumber || emptyPatientCurrentInsurance.groupNumber,

        //Financial Assistance
        waiverId: selectedPatientWaiver.waiverId || emptyPatientWaiver.waiverId,
        monthlyExpenses: selectedPatientWaiver.monthlyExpenses || emptyPatientWaiver.monthlyExpenses,
        householdSize: selectedPatientWaiver.householdSize || emptyPatientWaiver.householdSize,
        educationLevel: selectedPatientWaiver.educationLevel || emptyPatientWaiver.educationLevel,
        fplRatio: selectedPatientWaiver.fplRatio || emptyPatientWaiver.fplRatio,
        expenseIncomeRatio: selectedPatientWaiver.expenseIncomeRatio || emptyPatientWaiver.expenseIncomeRatio,
        scholarshipPercentage: selectedPatientWaiver.scholarshipPercentage || emptyPatientWaiver.scholarshipPercentage,
        expirationDt: selectedPatientWaiver.expirationDt || emptyPatientWaiver.expirationDt,
        selectedScholarshipConfig: selectedScholarshipConfig || null

    };

    return {
        initialValues,
    };
};

